import React, { useState, useEffect, useRef } from 'react';
import ReservationTimer from './ReservationTimer';
import TCell from '../../../frontend-common-libs/src/components/common/tables/TCell';
import { ImmutableMap, InstrumentItem } from '../../../frontend-common-libs/src/common/types';
import Checkbox from '../../../frontend-common-libs/src/common/checkbox';
import { getCurrentUserIdentityId } from '../../../frontend-common-libs/src/utils/authUtils';
import Reservation, { ReservationShadowState } from '../Reservation';
import ReservationFacade, { ReservationStatusEnum } from './ReservationFacade';
import { FleetToggleOptionsEnum } from '../FleetManagementOptions';
import { NullableString } from '../../../frontend-common-libs/src/common/nullable-types';
import { InstrumentStatusEnum } from '../../common/InstrumentFacade';
import notification from '../../../frontend-common-libs/src/utils/notifications';
import { ReservationProtocol } from '../types';
import Job from '../Job';
import updateReservationProtocol from './InstrumentFleetClickHandlers';
import noneProtocolItem from '../noneProtocolItem';
import { FleetManagementRow } from '../fleet-management-row';

export type Props = {
  instrument: ImmutableMap<InstrumentItem>;
  fleetToggleOption: string;
  selectAllCheckbox: boolean;
  updateSelectedInstruments: (id: string, select: boolean) => void;
  updateReservedInstruments: (id: string, reserved: boolean) => void;
  reservationProtocol: string;
  setReservationProtocol: React.Dispatch<React.SetStateAction<string>>;
};

export type LoadedState = {
  instrumentStatusLoaded: boolean;
  reservationShadowLoaded: boolean;
};

export type ReservationState = {
  instrumentStatus: NullableString;
  reservationShadowState: ReservationShadowState | null;
  protocol: NullableString;
};

export default function InstrumentFleetRow({
  instrument,
  fleetToggleOption,
  selectAllCheckbox,
  updateSelectedInstruments,
  updateReservedInstruments,
  reservationProtocol,
  setReservationProtocol
}: Props) {
  const userId: string = getCurrentUserIdentityId();
  const instrumentId = instrument.get('id');
  const instrumentName = instrument.get('name');

  const [loadedState, setLoadedState] = useState<LoadedState>({
    instrumentStatusLoaded: false,
    reservationShadowLoaded: false
  });
  const [reservationState, setReservationState] = useState<ReservationState>({
    instrumentStatus: null,
    reservationShadowState: null,
    protocol: null
  });
  const [updateReservationStatusFromFrontend, setUpdateReservationStatusFromFrontend] =
    useState<boolean>(false);
  const [updateReservationStatusFromInstrument, setUpdateReservationStatusFromInstrument] =
    useState<boolean>(false);
  const desiredReservationPayload = useRef({
    reserved: false,
    userID: '',
    metadata: { reserved: { timestamp: 0 }, userID: { timestamp: 0 } }
  });
  const [reservationCheckBox, setReservationCheckBox] = useState<boolean>(selectAllCheckbox);
  const initialRender = useRef(true);

  const reservationUpdateTopicCallback = (topic: string, payload: unknown): void => {
    const payloadJson = JSON.parse((payload as Buffer).toString());
    const clientToken = payloadJson?.clientToken;

    if (!clientToken) {
      setUpdateReservationStatusFromInstrument(true);
      return;
    }

    if (topic.includes(`/accepted`)) {
      if (clientToken.includes(`brio`)) {
        desiredReservationPayload.current = {
          ...payloadJson.state.desired.reservation,
          metadata: payloadJson.metadata.desired.reservation
        };
        setUpdateReservationStatusFromFrontend(true);
      } else if (clientToken.includes(`ptc`)) {
        setUpdateReservationStatusFromInstrument(true);
      }
    } else if (topic.includes(`/rejected`) && clientToken.includes(`brio`)) {
      notification.error(`Reservation failed. Check network connection and try again.`, {
        autoClose: false
      });
    }
  };

  const reservationFacade = new ReservationFacade({
    instrument,
    reservationState,
    userId,
    fleetRowShadowsLoaded: loadedState
  });

  const {
    isAllReservationShadowsLoaded,
    isInstrumentReservable,
    displayedReservationStatus,
    reservationStatusProperties,
    instrumentFacade,
    isInstrumentReservedByUser
  } = reservationFacade;

  const {
    modelName,
    instrumentStatus,
    details: { softwareVersion }
  } = instrumentFacade;

  const getProtocolFromJob = (reservationShadowState: ReservationShadowState) => {
    const { jobId } = reservationShadowState;
    if (reservationShadowState === null) return null;

    if (jobId) {
      return Job.getJobProtocolFromReservation(jobId);
    }
    return null;
  };

  const getReservationShadowState = async () => {
    try {
      const reservationShadowState = await Reservation.getCurrentReservation(instrumentId);
      const protocol = await getProtocolFromJob(reservationShadowState);
      setLoadedState(prevState => ({ ...prevState, reservationShadowLoaded: true }));
      setReservationState(prevState => ({
        ...prevState,
        reservationShadowState,
        protocol
      }));
    } catch (error) {
      setLoadedState(prevState => ({ ...prevState, reservationShadowLoaded: true }));
    }
  };

  useEffect(() => {
    if (instrumentStatus !== InstrumentStatusEnum.Connecting) {
      updateReservedInstruments(instrumentId, isInstrumentReservedByUser);
    }
  }, [isInstrumentReservedByUser]);

  useEffect(() => {
    let reservation: Reservation;
    if (
      reservationFacade.softwareVersionSupportsReservations(softwareVersion) &&
      (instrumentStatus === InstrumentStatusEnum.Connecting ||
        instrumentStatus === InstrumentStatusEnum.Idle)
    ) {
      (async () => {
        reservation = new Reservation();
        await reservation.disconnect(instrumentId);
        await reservation.connect(instrumentId, reservationUpdateTopicCallback);
      })();
    }

    return () => {
      (async () => {
        if (reservation) {
          await reservation.disconnect(instrumentId);
        }
      })();
    };
  }, []);

  useEffect(() => {
    if (
      updateReservationStatusFromFrontend &&
      reservationFacade.softwareVersionSupportsReservations(softwareVersion) &&
      instrumentStatus
    ) {
      if (reservationState.reservationShadowState?.desired === undefined) {
        getReservationShadowState();
      }

      const receivedShadowDesiredBlock = desiredReservationPayload.current;

      const currentReservationShadowState = reservationState.reservationShadowState;
      if (currentReservationShadowState?.desired) {
        const shadowDesiredBlock = currentReservationShadowState.desired;
        if (
          receivedShadowDesiredBlock.reserved !== undefined &&
          receivedShadowDesiredBlock.userID !== undefined
        ) {
          shadowDesiredBlock.reserved = receivedShadowDesiredBlock.reserved;
          shadowDesiredBlock.userId = receivedShadowDesiredBlock.userID;
          shadowDesiredBlock.metadata = {
            userId: receivedShadowDesiredBlock.metadata.userID.timestamp,
            reserved: receivedShadowDesiredBlock.metadata.reserved.timestamp
          };

          const newReservationShadowState = {
            ...currentReservationShadowState,
            desired: shadowDesiredBlock
          };

          setReservationState(prevState => ({
            ...prevState,
            reservationShadowState: newReservationShadowState
          }));

          updateSelectedInstruments(instrumentId, false);
        }
      }
      setLoadedState(prevState => ({ ...prevState, reservationShadowLoaded: true }));
      setUpdateReservationStatusFromFrontend(false);
    }
  }, [updateReservationStatusFromFrontend]);

  useEffect(() => {
    if (
      reservationFacade.softwareVersionSupportsReservations(softwareVersion) &&
      instrumentStatus
    ) {
      getReservationShadowState();
      setUpdateReservationStatusFromInstrument(false);
    }
  }, [updateReservationStatusFromInstrument]);

  useEffect(() => {
    if (
      reservationFacade.softwareVersionSupportsReservations(softwareVersion) &&
      instrumentStatus
    ) {
      setLoadedState(prevState => ({ ...prevState, instrumentStatusLoaded: true }));
      setReservationState(prevState => ({ ...prevState, instrumentStatus }));
    }
  }, [instrumentStatus]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else {
      setReservationCheckBox(false);
    }
  }, [fleetToggleOption]);

  const handleSelectInstrument = (): void => {
    if (fleetToggleOption === FleetToggleOptionsEnum.Manage) {
      const updatedReservationProtocol = updateReservationProtocol(
        instrumentId,
        reservationState?.reservationShadowState?.jobId ?? '',
        reservationState?.protocol ?? '',
        reservationProtocol
      );
      setReservationProtocol(updatedReservationProtocol);
    }

    setReservationCheckBox(!reservationCheckBox);
    updateSelectedInstruments(instrumentId, !reservationCheckBox);
  };

  const canCheckboxBeDisplayed = (): boolean =>
    (fleetToggleOption === FleetToggleOptionsEnum.Create && isInstrumentReservable) ||
    (fleetToggleOption === FleetToggleOptionsEnum.Manage &&
      isInstrumentReservedByUser &&
      displayedReservationStatus !== ReservationStatusEnum.CancellationPending);

  useEffect(() => {
    if (canCheckboxBeDisplayed()) {
      setReservationCheckBox(selectAllCheckbox);
      updateSelectedInstruments(instrumentId, selectAllCheckbox);
    }
  }, [selectAllCheckbox]);

  const canRowBeDisplayed = (): boolean =>
    fleetToggleOption === FleetToggleOptionsEnum.Create ||
    (isInstrumentReservedByUser && fleetToggleOption === FleetToggleOptionsEnum.Manage);

  const renderCheckbox = () => {
    if (isAllReservationShadowsLoaded) {
      if (canCheckboxBeDisplayed()) {
        return (
          <Checkbox
            id={`checkbox_${instrumentId}`}
            name={`checkbox_${instrumentId}`}
            value={reservationCheckBox}
            checked={reservationCheckBox}
            onChange={handleSelectInstrument}
          />
        );
      }
    }
    return null;
  };

  const renderCheckboxCell = () => {
    return <TCell>{renderCheckbox()}</TCell>;
  };

  const renderReservationStatus = (): React.JSX.Element => {
    let reservationStatusText = InstrumentStatusEnum.Connecting.toString();
    if (isAllReservationShadowsLoaded) {
      reservationStatusText = displayedReservationStatus;
    }
    return (
      <>
        <img
          src={reservationStatusProperties.src}
          alt={reservationStatusProperties.alt}
          className="status-img"
        />
        {reservationStatusText}
      </>
    );
  };

  const getProtocolName = (): string => {
    let protocolAssigned = reservationState?.protocol ?? noneProtocolItem.name;
    const reservationProtocolArray = JSON.parse(reservationProtocol) as ReservationProtocol[];

    reservationProtocolArray.forEach(item => {
      if (instrumentId === item.instrumentId) {
        protocolAssigned = item.protocolName;
      }
    });

    return protocolAssigned;
  };

  const renderProtocolNameCell = () => {
    const protocolNameText = getProtocolName();
    return <TCell className="cell-text">{protocolNameText}</TCell>;
  };

  const renderAdditionalColumns = () => {
    return (
      <>
        <TCell className="cell-text">
          {renderReservationStatus()}
          <ReservationTimer
            instrument={instrument}
            reservationStatus={displayedReservationStatus}
            reservationTimeout={reservationFacade.reservationPendingTimeout()}
            cancellationTimeout={reservationFacade.cancellationPendingTimeout()}
          />
        </TCell>
        {fleetToggleOption === FleetToggleOptionsEnum.Manage ? renderProtocolNameCell() : null}
      </>
    );
  };

  const render = () => {
    if (
      reservationFacade.softwareVersionSupportsReservations(softwareVersion) &&
      reservationFacade.modelSupportsReservations() &&
      instrumentStatus &&
      canRowBeDisplayed()
    ) {
      return (
        <FleetManagementRow
          instrumentId={instrumentId}
          instrumentType={instrument.get('type')}
          modelName={modelName}
          instrumentName={instrumentName}
        >
          {renderCheckboxCell()}
          {renderAdditionalColumns()}
        </FleetManagementRow>
      );
    }
    return null;
  };

  return render();
}
